/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';

interface ReferenceDetailDialogProps {
  open: boolean;
  onClose: () => void;
  referenceDetail: { name: string; snippet: string } | null;
}

const DialogHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
}));

const SummaryTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
}));

const ReferenceDetailDialog: React.FC<ReferenceDetailDialogProps> = ({
  open,
  onClose,
  referenceDetail,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogHeader>
        <Typography variant="h6">
          {t('referenceDetail.summaryOf', { name: referenceDetail?.name })}
        </Typography>
        <Button onClick={onClose}>&times;</Button>
      </DialogHeader>
      <DialogContent dividers>
        <SummaryTitle variant="h6">{t('referenceDetail.preview')}</SummaryTitle>
        <DialogContentText>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {referenceDetail?.snippet}
          </ReactMarkdown>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('referenceDetail.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReferenceDetailDialog;
